import React, { Suspense } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import CodeEditor from '@/components/code/CodeEditor';
import CodeHistory from '@/components/code/CodeHistory';
// Lazy: fabric.js touches DOMMatrix at module init, which breaks SSR.
// Keeping this off the SSR bundle.
const DrawingCanvas = React.lazy(() => import('@/components/canvas/DrawingCanvas'));
import ChatHistorySidebar from '@/components/chat/ChatHistorySidebar';
import { Message } from '@/types';
import { useIsMobile } from '@/hooks/use-mobile';

// Define the CodeSnippet interface
export interface CodeSnippet {
  id: string;
  code: string;
  language: string;
  name: string;
  chatId: string | null;
  timestamp: string;
  userId?: string;
  content: string;
}

interface ChatDialogsProps {
  isCodeEditorOpen: boolean;
  setIsCodeEditorOpen: (open: boolean) => void;
  isCodeHistoryOpen: boolean;
  setIsCodeHistoryOpen: (open: boolean) => void;
  isCanvasOpen: boolean;
  setIsCanvasOpen: (open: boolean) => void;
  isChatHistoryOpen: boolean;
  setIsChatHistoryOpen: (open: boolean) => void;
  onSaveCode: (code: string, language: string) => void;
  onSelectCodeSnippet: (snippetId: string) => void;
  onSaveCanvas: (imageData: string) => void;
  onSelectChatHistory: (messages: Message[]) => void;
  codeSnippets: CodeSnippet[];
  onInsertSnippetToChat?: (snippet: CodeSnippet) => void; // NEW PROP
}

const ChatDialogs: React.FC<ChatDialogsProps> = ({
  isCodeEditorOpen,
  setIsCodeEditorOpen,
  isCodeHistoryOpen,
  setIsCodeHistoryOpen,
  isCanvasOpen,
  setIsCanvasOpen,
  isChatHistoryOpen,
  setIsChatHistoryOpen,
  onSaveCode,
  onSelectCodeSnippet,
  onSaveCanvas,
  onSelectChatHistory,
  codeSnippets,
  onInsertSnippetToChat // NEW PROP
}) => {
  const isMobile = useIsMobile();
  
  return (
    <>
      <Dialog open={isCodeEditorOpen} onOpenChange={setIsCodeEditorOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Code Editor</DialogTitle>
          </DialogHeader>
          <CodeEditor 
            onSave={onSaveCode} 
            codeSnippets={codeSnippets} 
            onSelectSnippet={onSelectCodeSnippet}
            showHistory={true}
            onInsertSnippetToChat={onInsertSnippetToChat}
            onClose={() => setIsCodeEditorOpen(false)}
          />
        </DialogContent>
      </Dialog>
      
      <Dialog open={isCodeHistoryOpen} onOpenChange={setIsCodeHistoryOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Code Snippets</DialogTitle>
          </DialogHeader>
          <CodeHistory codeSnippets={codeSnippets} onSelectSnippet={onSelectCodeSnippet} />
        </DialogContent>
      </Dialog>
      
      <Dialog open={isCanvasOpen} onOpenChange={setIsCanvasOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Drawing Canvas</DialogTitle>
          </DialogHeader>
          <Suspense fallback={null}>
            <DrawingCanvas onSave={onSaveCanvas} />
          </Suspense>
        </DialogContent>
      </Dialog>
      
      <Sheet open={isChatHistoryOpen} onOpenChange={setIsChatHistoryOpen}>
        <SheetContent 
          side="right" 
          className={isMobile ? "w-full p-0" : "w-[400px] sm:w-[540px]"}
          // Hide the default close button that comes with SheetContent
          hideCloseButton={isMobile}
        >
          {!isMobile && (
            <SheetHeader>
              <SheetTitle>Chat History</SheetTitle>
            </SheetHeader>
          )}
          <div className={isMobile ? "h-full" : "h-[calc(100vh-80px)]"}>
            <ChatHistorySidebar 
              onSelectHistory={onSelectChatHistory} 
              onClose={() => setIsChatHistoryOpen(false)} 
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ChatDialogs;